import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import { useContent } from "../../lib/contentContext";

const SIZE = "&w=640&h=640&fit=crop";
const IMAGES = [
    "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?crop=entropy&cs=srgb&fm=jpg&q=85&ixlib=rb-4.1.0",
    "https://images.unsplash.com/photo-1643684391140-c5056cfd3436?crop=entropy&cs=srgb&fm=jpg&q=85&ixlib=rb-4.1.0",
    "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?crop=entropy&cs=srgb&fm=jpg&q=85&ixlib=rb-4.1.0",
    "https://images.unsplash.com/photo-1648203276014-20f97ba1f817?crop=entropy&cs=srgb&fm=jpg&q=85&ixlib=rb-4.1.0",
    "https://images.unsplash.com/photo-1785860945533-918a531bcdeb?crop=entropy&cs=srgb&fm=jpg&q=85&ixlib=rb-4.1.0",
    "https://images.unsplash.com/photo-1785861084191-3600dfc2a6d6?crop=entropy&cs=srgb&fm=jpg&q=85&ixlib=rb-4.1.0",
    "https://images.unsplash.com/photo-1785861485926-93a13556d656?crop=entropy&cs=srgb&fm=jpg&q=85&ixlib=rb-4.1.0",
    "https://images.unsplash.com/photo-1761819922656-d1b77eef49c0?crop=entropy&cs=srgb&fm=jpg&q=85&ixlib=rb-4.1.0",
].map((u) => u + SIZE);

export default function InstagramFeed() {
    const { content } = useContent();
    const igUrl = content?.contact?.social?.instagram || "#";
    const handle = (() => {
        try {
            const parts = igUrl.replace(/\/+$/, "").split("/");
            const last = parts[parts.length - 1];
            return last && last !== "instagram.com" ? `@${last}` : "@overallbeauty";
        } catch {
            return "@overallbeauty";
        }
    })();

    const Tile = ({ src, i }) => (
        <a
            href={igUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`instagram-tile-${i}`}
            className="group relative block h-56 w-56 shrink-0 overflow-hidden sm:h-72 sm:w-72"
            aria-label={`View ${handle} on Instagram`}
        >
            <img
                src={src}
                alt={`${handle} — treatment ${i + 1}`}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-ink/50 opacity-0 backdrop-blur-[2px] transition-opacity duration-500 group-hover:opacity-100">
                <Instagram className="h-8 w-8 text-gold" />
            </div>
        </a>
    );

    const row = (
        <div className="flex shrink-0 items-stretch gap-3 pr-3">
            {IMAGES.map((src, i) => (
                <Tile key={i} src={src} i={i} />
            ))}
        </div>
    );

    return (
        <section
            className="relative overflow-hidden border-t border-white/10 bg-ink py-16 sm:py-20"
            data-testid="instagram-feed-section"
            aria-label="Instagram feed"
        >
            <div className="mx-auto max-w-[1600px] px-4 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="mb-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end"
                >
                    <div>
                        <div className="label text-gold">Follow along</div>
                        <h2 className="mt-3 font-serif text-3xl italic leading-[1.05] text-white sm:text-5xl">
                            The transformations,
                            <br className="hidden sm:block" /> on Instagram.
                        </h2>
                    </div>
                    <a
                        href={igUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="instagram-follow-cta"
                        className="group inline-flex items-center gap-3 border border-gold/50 px-5 py-3 text-[11px] uppercase tracking-[0.24em] text-white transition-colors duration-500 hover:bg-gold hover:text-ink"
                    >
                        <Instagram className="h-4 w-4" />
                        {handle}
                    </a>
                </motion.div>
            </div>

            <div className="relative">
                <div className="marquee-track">
                    {row}
                    {row}
                </div>
                <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-ink to-transparent sm:w-40" />
                <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-ink to-transparent sm:w-40" />
            </div>
        </section>
    );
}
